import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePreferences, PreferenceActions, PreferenceSections } from './preferences';
import { getAuthUser } from 'store/auth/selectors';
import { getOrgMappedReturns } from 'store/organisation/selectors';
import { turnModalOn } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { ModalData } from 'store/app/types';
import { LeaveReviewModal } from 'containers/LeaveReviewModal';
import GenTaxImage from 'assets/images/gen-tax-img.svg';
import {
  hasSubmittedReturn,
  shouldShowReviewModal,
  REVIEW_MODAL_LATER_DATE,
  REVIEW_MODAL_NO_THANKS,
} from './review-modal-utils';
import i18n from 'i18next';
/**
 * Hook to check and show review modal if conditions are met
 */
export const useReviewModal = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(getAuthUser);
  const mappedReturns = useSelector(getOrgMappedReturns);
  const preferences = usePreferences();

  const showReviewModal = () => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: i18n.t('navigation.leave-a-review'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <LeaveReviewModal
          onAlreadyDid={() => {
            // Mark as "already did" - same as No Thanks
            if (currentUser) {
              preferences(
                PreferenceActions.SET,
                PreferenceSections.MAIN,
                { name: REVIEW_MODAL_NO_THANKS, value: true },
                currentUser.userUuid
              );
            }
          }}
          onLater={() => {
            // Set "Later" date to today (user will see it again tomorrow)
            if (currentUser) {
              const today = new Date();
              preferences(
                PreferenceActions.SET,
                PreferenceSections.MAIN,
                { name: REVIEW_MODAL_LATER_DATE, value: today.toISOString() },
                currentUser.userUuid
              );
            }
          }}
          onNoThanks={() => {
            // Mark as "No Thanks"
            if (currentUser) {
              preferences(
                PreferenceActions.SET,
                PreferenceSections.MAIN,
                { name: REVIEW_MODAL_NO_THANKS, value: true },
                currentUser.userUuid
              );
            }
          }}
        />
      ),
    };
    dispatch(turnModalOn(modalData));
  };

  const checkAndShowReviewModal = () => {
    if (!currentUser || !mappedReturns) {
      return;
    }

    // Check if user has submitted at least one return
    if (!hasSubmittedReturn(mappedReturns)) {
      return;
    }

    // Get preferences
    const reviewLaterDate = preferences(
      PreferenceActions.GET,
      PreferenceSections.MAIN,
      { name: REVIEW_MODAL_LATER_DATE },
      currentUser.userUuid
    );
    const reviewNoThanks = preferences(
      PreferenceActions.GET,
      PreferenceSections.MAIN,
      { name: REVIEW_MODAL_NO_THANKS },
      currentUser.userUuid
    );

    // Check if modal should be shown
    if (shouldShowReviewModal(reviewLaterDate, reviewNoThanks)) {
      showReviewModal();
    }
  };

  return { checkAndShowReviewModal, showReviewModal };
};

